import React from 'react';
import { connect } from 'react-redux';
import SpendYourFileContainer from 'components/Spend/YourSpendFile/SpendYourFileContainer';
import ClassificationSummary from 'components/Spend/ClassificationAndTopDetails/ClassificationSummary';
import TopDetailsContainer from 'components/Spend/ClassificationAndTopDetails/TopDetailsContainer';
import ProgramEnablementAnalysis from 'components/Spend/ProgramEnablementSummary/ProgramEnablementAnalysis';
import AribaNetworkEnablementAnalysis from 'components/Spend/ProgramEnablementSummary/AribaNetworkEnablementAnalysis';
import GanntChartContainer from 'components/Spend/GantChart/GanntChartContainer';
import EnablementWavesTable from 'components/Spend/EnablementInsight/EnablementWavesTable';
import AccountParameters from 'components/Spend/YourSpendFile/AccountParameters';
import SubTabsPanel from '../Tabs/SubTabsPanel';
import { redirectFromEnablementTab } from 'actions/spendActions';
import KPIDashboardV2 from 'components/Kpi/KPIDashboardV2';
import Saving from 'components/Saving/Saving';
import SavingOptions from 'components/Saving/SavingOptions';
import SavingGraph from 'components/Saving/SavingGraph';
import KPIDashboardManualV2 from 'components/Kpi/KPIDashboardManualV2';
import KPIChartsV2 from 'components/Kpi/KPIChartsV2';
import EnablementStrategyWizardContainer from './YourSpendFile/EnablementStrategyWizardContainer';
import EnablementRecommendations from 'components/Spend/EnablementInsight/EnablementRecommendations';
import { setSubTabMenu } from 'actions/userActions';
import { GetRiseSavingDetails } from 'actions/kpiActions';

const OPTIONS_TAB_ID = 9999; // stable id for "Options" tab so focus doesn't move when tabs change

class SubTabsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();

    // guard to avoid concurrent fetching
    this._loadingSavingTabs = false;

    const initialReq = props.spendDetails && props.spendDetails.currentActiveRequest;
    this.state = {
      isRequestFileActive: !!initialReq,
      tabId: 1,
      tabs: [],
      // cached saving rows so we can rebuild tabs without refetch on save
      savingData: null,
      // when API returns a message (Table1.MSG) we store it here
      savingMessage: null
    };

    this.dispatchTabChange = this.dispatchTabChange.bind(this);
    this.intializeSupplierTabs = this.intializeSupplierTabs.bind(this);
    this.loadSavingTabs = this.loadSavingTabs.bind(this);
    this.buildSavingTabsFromData = this.buildSavingTabsFromData.bind(this);
  }

  componentDidMount() {
    this.intializeSupplierTabs();
  }

  componentDidUpdate(prevProps) {
    const prevReq = prevProps.spendDetails && prevProps.spendDetails.currentActiveRequest;
    const currReq = this.props.spendDetails && this.props.spendDetails.currentActiveRequest;
  
    const prevDmc = prevReq ? prevReq.DMC_ID : undefined;
    const currDmc = currReq ? currReq.DMC_ID : undefined;
  
    const prevVs = prevReq ? prevReq.VS_ID : undefined;
    const currVs = currReq ? currReq.VS_ID : undefined;
  
    const paramChanged = prevProps.param !== this.props.param;
    const dmcChanged = prevDmc !== currDmc;
    const vsChanged = prevVs !== currVs;
    const requestPresenceChanged = (!!prevReq) !== (!!currReq);
  
    if (paramChanged || dmcChanged || vsChanged || requestPresenceChanged) {
      // normalize to boolean
      const isReqActive = !!currReq;
      // only set state and re-init when the boolean actually changed to avoid extra renders
      if (this.state.isRequestFileActive !== isReqActive) {
        this.setState({ isRequestFileActive: isReqActive }, () => {
          this.intializeSupplierTabs();
        });
      } else {
        // boolean already correct, just re-init tabs because other relevant prop changed
        this.intializeSupplierTabs();
      }
    } else {
      // No relevant prop change — ensure the boolean flag is normalized (true/false)
      // but only update state when it actually differs (prevents unnecessary setState)
      const normalized = !!currReq;
      if (this.state.isRequestFileActive !== normalized) {
        this.setState({ isRequestFileActive: normalized }, () => {
            this.intializeSupplierTabs();
          });
      }
      // otherwise do nothing
    }
  }

  /**
   * Build Saving tabs from provided rows.
   * Options tab uses a stable id OPTIONS_TAB_ID so focus stays when dynamic tabs removed.
   */
  buildSavingTabsFromData(rows = [], focusOptions = false) {
    const grouped = {};
    (rows || []).forEach((r) => {
      const saving = r && r.SAVING_FIELD_NAME ? r.SAVING_FIELD_NAME : '(No Saving Field)';
      if (!grouped[saving]) grouped[saving] = [];
      grouped[saving].push(r);
    });

    const tabs = [{ id: 1, label: 'Summary', component: (<SavingGraph key={`savinggraph-${(rows && rows.length) || 0}`} />) }];

    let nextId = 2;
    Object.keys(grouped).forEach((savingName) => {
      const items = grouped[savingName];
      const anyAvailable = items.some(it => {
        if (typeof it.FIELD_AVAILABLE === 'string') return it.FIELD_AVAILABLE.toLowerCase() === 'yes';
        return !!it.FIELD_AVAILABLE;
      });
      if (anyAvailable) {
        tabs.push({
          id: nextId++,
          label: savingName,
          component: (<Saving key={`saving-${savingName}-${(rows && rows.length) || 0}`} param={savingName} options={items} />)
        });
      }
    });

    // stable options tab id
    tabs.push({
      id: OPTIONS_TAB_ID,
      label: 'Options',
      component: (<SavingOptions key={`saving-options-${(rows && rows.length) || 0}`} initialData={rows} onSaved={() => this.loadSavingTabs(true, true)} />)
    });

    // compute restored tab id:
    // - if focusOptions true -> OPTIONS_TAB_ID
    // - else try to keep current tabId if present, otherwise fall back to first tab
    const validTabIds = tabs.map(t => t.id);
    const currentTabId = this.state.tabId || 1;
    let restoredTabId;
    if (focusOptions) {
      restoredTabId = OPTIONS_TAB_ID;
    } else if (validTabIds.includes(currentTabId)) {
      restoredTabId = currentTabId;
    } else {
      restoredTabId = validTabIds.length ? validTabIds[0] : 1;
    }

    // only update state when actual changes to prevent re-renders
    if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== restoredTabId || this.state.savingMessage !== null) {
      this.setState({ tabs, tabId: restoredTabId, savingData: rows, savingMessage: null });
    } else {
      // still update cached savingData (no full tab change) so future saves use fresh cache
      if (this.state.savingData !== rows) {
        this.setState({ savingData: rows });
      }
    }
  }

  /**
   * Load saving tabs - uses cached savingData if present unless forceReload=true.
   * focusOptions=true will set active tab to the stable Options tab.
   *
   * IMPORTANT: If API returns Table1.MSG (non-empty), we hide all Saving sub-tabs and display the message instead.
   */
  async loadSavingTabs(focusOptions = false, forceReload = false) {
    // use cache if available and not forcing reload
    if (!forceReload && this.state.savingData && Array.isArray(this.state.savingData) && this.state.savingMessage == null) {
      this.buildSavingTabsFromData(this.state.savingData, focusOptions);
      return;
    }

    if (this._loadingSavingTabs) return; // avoid concurrent loads
    this._loadingSavingTabs = true;

    try {
      const spendDetails = this.props.spendDetails;
      const userEmail = this.props.user && this.props.user.user && this.props.user.user.Email;

      if (!spendDetails || !spendDetails.currentActiveRequest || !userEmail) {
        // fallback minimal tabs
        this.buildSavingTabsFromData([], focusOptions);
        return;
      }

      const DMC_ID = spendDetails.currentActiveRequest.DMC_ID;
      const VS_ID = spendDetails.currentActiveRequest.VS_ID;

      let respOrPromise;
      try {
        respOrPromise = this.props.GetRiseSavingDetails(DMC_ID, VS_ID, userEmail);
      } catch (err) {
        respOrPromise = null;
      }
      const resp = (respOrPromise && typeof respOrPromise.then === 'function') ? await respOrPromise : respOrPromise;

      // defensively extract Table and Table1
      const result = resp && resp.data && resp.data.Result ? resp.data.Result : null;
      const tableRows = result && Array.isArray(result.Table) ? result.Table : [];
      const table1 = result && Array.isArray(result.Table1) ? result.Table1 : [];

      // Check MSG in Table1 (defensive)
      const msg = (table1 && table1.length && table1[0] && typeof table1[0].MSG !== 'undefined') ? (table1[0].MSG || '') : '';

      if (typeof msg === 'string' && msg.trim().length > 0) {
        // When MSG is present => hide all saving sub-tabs and display the message on screen
        const messageText = msg.trim();

        // Build a single tab to display the message
        const tabs = [
          {
            id: 1,
            label: 'Summary',
            component: (
              <div key="saving-message" className="saving-message grid-x errors" style={{ padding: 20 }}>
                {/* render as plaintext to avoid XSS; if msg contains HTML and you trust it, you can use dangerouslySetInnerHTML */}
                <div className="medium-12">
                  {messageText.split('\n').map((line, idx) => <div key={idx}>{line}</div>)}
                </div>
              </div>
            )
          }
        ];

        // set state: show message tab, clear cached savingData (we don't want to rebuild the normal tabs)
        this.setState({ tabs, tabId: 1, savingData: [], savingMessage: messageText });
        return;
      }

      // No message: proceed as normal using tableRows
      // cache and build
      this.setState({ savingData: tableRows, savingMessage: null }, () => {
        this.buildSavingTabsFromData(tableRows, focusOptions);
      });
    } catch (err) {
      // fallback
      this.buildSavingTabsFromData([], focusOptions);
    } finally {
      this._loadingSavingTabs = false;
    }
  }

  intializeSupplierTabs() {
    let tabs = [];
    const isReqActive = !!this.state.isRequestFileActive;

    if (this.props.param === this.props.content.yourFile.title) {
      if (isReqActive) {
        tabs = [
          { id: 1, label: "Value Story", component: (<SpendYourFileContainer history={this.props.history} />) },
          { id: 2, label: this.props.content.accountParameters.spendParameters, component: (<AccountParameters />) },
          { id: 3, label: this.props.content.prioritizationWizard.title, component: (<EnablementStrategyWizardContainer history={this.props.history} />) },
          { id: 4, label: "Enablement Recommendations", component: (<div className="grid-x"><EnablementRecommendations filterData={this.state.filterValues} /></div>) }
        ];
      } else {
        tabs = [{ id: 1, label: "Value Story", component: (<SpendYourFileContainer history={this.props.history} />) }];
      }
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    if (this.props.param === this.props.content.results.title) {
      tabs = [
        { id: 1, label: this.props.content.results.classificationSummary, component: (<ClassificationSummary />) },
        { id: 2, label: this.props.content.topDetails.title, component: (<TopDetailsContainer />) }
      ];
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    if (this.props.param === this.props.content.enablement.title) {
      tabs = [
        { id: 1, label: this.props.content.enablement.title, component: (<ProgramEnablementAnalysis />) },
        { id: 2, label: this.props.content.enablement.aribaNetworkMatch, component: (<AribaNetworkEnablementAnalysis />) }
      ];
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    if (this.props.param === this.props.ganntTexts.title) {
      tabs = [
        { id: 1, label: this.props.ganntTexts.title, component: (<GanntChartContainer />) },
        { id: 2, label: this.props.ganntTexts.enablementStrategy, component: (<EnablementWavesTable />) }
      ];
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    if (this.props.param === 'KPI') {
      tabs = [
        { id: 1, label: 'KPI Dashboard', component: (<KPIDashboardV2 />) },
        { id: 3, label: 'KPI Charts', component: (<KPIChartsV2 />) }
      ];
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    if (this.props.param === 'Saving') {
      // Use cache if possible; otherwise fetch
      this.loadSavingTabs(false, false);
      return;
    }

    if (this.props.param === 'KPI-V2') {
      tabs = [
        { id: 1, label: 'KPI Dashboard', component: (<KPIDashboardManualV2 />) },
        { id: 3, label: 'KPI Charts', component: (<KPIChartsV2 />) }
      ];
      if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
        this.setState({ tabs, tabId: 1 });
      }
      return;
    }

    // default fallback
    if (!this._areTabsEqual(this.state.tabs, tabs) || this.state.tabId !== 1) {
      this.setState({ tabs: tabs, tabId: 1 });
    }
  }

  // shallow compare tabs
  _areTabsEqual(a = [], b = []) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!a[i] || !b[i]) return false;
      if (a[i].id !== b[i].id) return false;
      if (a[i].label !== b[i].label) return false;
    }
    return true;
  }

  dispatchTabChange(tabId) {
    this.props.setSubTabMenu(tabId);
    this.setState({ tabId: tabId });
  }

  componentWillUnmount() {
    this.props.redirectFromEnablementTab(false);
  }

  render() {
    return (
      <div className="grid-x">
        <div className="medium-12">
          <SubTabsPanel
            tabs={this.state.tabs}
            tabId={this.props.redirectFlag ? 3 : this.state.tabId}
            dispatchTabChange={this.dispatchTabChange}
            dispatchLastButtonClick={this.dispatchLastButtonClick}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    spendDetails: state.spendDetails,
    content: state.content.spendAnalysis,
    user: state.default,
    ganntTexts: state.content.ganntChartTab,
    redirectFlag: state.spendDetails.isRedirected,
    subTabMenu: state.setSubTabMenu
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    redirectFromEnablementTab: (flag) => dispatch(redirectFromEnablementTab(flag)),
    setSubTabMenu: (val) => dispatch(setSubTabMenu(val)),
    GetRiseSavingDetails: (DMCId, VSId, userEmail) => dispatch(GetRiseSavingDetails(DMCId, VSId, userEmail))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SubTabsContainer);
